import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2 } from 'lucide-react'
import { advApi, clientsApi } from '@/services/api'
import { getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import type { AdCampaign, CampaignType, Platform, Client } from '@/types'

// ---------------------------------------------------------------
// Types
// ---------------------------------------------------------------

interface CampaignFormData {
  name: string
  clientId: string
  type: CampaignType
  platforms: Platform[]
  totalBudget: string
  startDate: string
  endDate: string
  objective: string
  targetAudience: string
  notes: string
}

interface CampaignFormModalProps {
  campaign?: AdCampaign
  onClose: () => void
  onSuccess?: () => void
}

// ---------------------------------------------------------------
// Constants
// ---------------------------------------------------------------

const PLATFORM_OPTIONS: { value: Platform; label: string }[] = [
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'FACEBOOK', label: 'Facebook' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'TWITTER', label: 'Twitter / X' },
  { value: 'TIKTOK', label: 'TikTok' },
  { value: 'YOUTUBE', label: 'YouTube' },
  { value: 'PINTEREST', label: 'Pinterest' },
]

const TYPE_OPTIONS: { value: CampaignType; label: string; description: string }[] = [
  { value: 'ALWAYS_ON', label: 'Always On', description: 'Campagna continuativa senza data di fine fissa' },
  { value: 'SPOT', label: 'Spot', description: 'Campagna limitata nel tempo con obiettivo specifico' },
]

const INITIAL_DATA: CampaignFormData = {
  name: '',
  clientId: '',
  type: 'ALWAYS_ON',
  platforms: [],
  totalBudget: '',
  startDate: '',
  endDate: '',
  objective: '',
  targetAudience: '',
  notes: '',
}

// ---------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------

function FormField({
  label,
  required,
  children,
  error,
  hint,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  error?: string | undefined
  hint?: string | undefined
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-danger-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
      {error && <p className="text-xs text-danger-600 mt-1">{error}</p>}
    </div>
  )
}

const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

const inputErrorClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-danger-300 rounded-lg',
  'focus:outline-none focus:border-danger-500 focus:ring-1 focus:ring-danger-500/20',
  'placeholder:text-slate-400 transition-colors'
)

// ---------------------------------------------------------------
// Main component
// ---------------------------------------------------------------

export function CampaignFormModal({ campaign, onClose, onSuccess }: CampaignFormModalProps) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(campaign)

  // Build initial form data from existing campaign if editing
  const [formData, setFormData] = useState<CampaignFormData>(() => {
    if (campaign) {
      return {
        name: campaign.name,
        clientId: campaign.clientId,
        type: campaign.type,
        platforms: campaign.platforms,
        totalBudget: String(campaign.totalBudget),
        startDate: campaign.startDate ? campaign.startDate.slice(0, 10) : '',
        endDate: campaign.endDate ? campaign.endDate.slice(0, 10) : '',
        objective: campaign.objective ?? '',
        targetAudience: '',
        notes: '',
      }
    }
    return INITIAL_DATA
  })

  const [errors, setErrors] = useState<Partial<Record<keyof CampaignFormData | 'global', string | undefined>>>({})
  const [successMsg, setSuccessMsg] = useState('')

  // Fetch clients for dropdown
  const { data: clientsResponse, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients', { limit: 100 }],
    queryFn: () => clientsApi.list({ limit: 100 }),
  })

  // Extract clients array from response
  const clients: Client[] = (() => {
    const raw = (clientsResponse as any)?.data
    if (Array.isArray(raw)) return raw
    if (raw?.data && Array.isArray(raw.data)) return raw.data
    if (raw?.items && Array.isArray(raw.items)) return raw.items
    return []
  })()

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => advApi.createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      setSuccessMsg('Campagna creata con successo!')
      setTimeout(() => {
        onSuccess?.()
        onClose()
      }, 800)
    },
    onError: (err) => {
      setErrors((prev) => ({ ...prev, global: getApiErrorMessage(err) }))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      advApi.updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] })
      setSuccessMsg('Campagna aggiornata con successo!')
      setTimeout(() => {
        onSuccess?.()
        onClose()
      }, 800)
    },
    onError: (err) => {
      setErrors((prev) => ({ ...prev, global: getApiErrorMessage(err) }))
    },
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, isPending])

  // ---------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------
  function validate(): boolean {
    const newErrors: Partial<Record<keyof CampaignFormData, string>> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Il nome della campagna e\' obbligatorio'
    }
    if (!formData.clientId) {
      newErrors.clientId = 'Seleziona un cliente'
    }
    if (!formData.type) {
      newErrors.type = 'Seleziona il tipo di campagna'
    }
    if (formData.platforms.length === 0) {
      newErrors.platforms = 'Seleziona almeno una piattaforma'
    }
    const budget = parseFloat(formData.totalBudget)
    if (!formData.totalBudget || isNaN(budget) || budget <= 0) {
      newErrors.totalBudget = 'Il budget deve essere un numero positivo'
    }
    if (!formData.startDate) {
      newErrors.startDate = 'La data di inizio e\' obbligatoria'
    }
    if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
      newErrors.endDate = 'La data di fine deve essere successiva alla data di inizio'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // ---------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------

  function handleChange<K extends keyof CampaignFormData>(field: K, value: CampaignFormData[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
    if (errors.global) {
      setErrors((prev) => ({ ...prev, global: undefined }))
    }
  }

  function togglePlatform(platform: Platform) {
    const next = formData.platforms.includes(platform)
      ? formData.platforms.filter((p) => p !== platform)
      : [...formData.platforms, platform]
    handleChange('platforms', next)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      clientId: formData.clientId,
      type: formData.type,
      platforms: formData.platforms,
      totalBudget: parseFloat(formData.totalBudget),
      startDate: formData.startDate || undefined,
      endDate: formData.endDate || undefined,
      objective: formData.objective.trim() || undefined,
      description: formData.notes.trim() || undefined,
    }

    if (isEdit && campaign) {
      updateMutation.mutate({ id: campaign.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  // ---------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => { if (!isPending) onClose() }}
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-3xl shadow-modal border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {isEdit ? 'Modifica Campagna' : 'Nuova Campagna'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEdit
                ? 'Aggiorna i dettagli della campagna pubblicitaria'
                : 'Crea una nuova campagna pubblicitaria'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => { if (!isPending) onClose() }}
            disabled={isPending}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">

            {/* Global error */}
            {errors.global && (
              <div className="flex items-start gap-2 p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <span className="text-xs text-danger-700 flex-1">{errors.global}</span>
              </div>
            )}

            {/* Success message */}
            {successMsg && (
              <div className="flex items-start gap-2 p-3 bg-success-50 border border-success-200 rounded-lg">
                <span className="text-xs text-success-700 flex-1">{successMsg}</span>
              </div>
            )}

            {/* Campaign name */}
            <FormField label="Nome Campagna" required error={errors.name}>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="es. Brand Awareness – Cliente SS2026"
                className={errors.name ? inputErrorClass : inputClass}
                disabled={isPending}
              />
            </FormField>

            {/* Client + Type */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Cliente" required error={errors.clientId}>
                <select
                  value={formData.clientId}
                  onChange={(e) => handleChange('clientId', e.target.value)}
                  className={errors.clientId ? inputErrorClass : inputClass}
                  disabled={isPending || clientsLoading}
                >
                  <option value="">
                    {clientsLoading ? 'Caricamento...' : 'Seleziona cliente'}
                  </option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </FormField>

              <FormField label="Tipo Campagna" required error={errors.type}>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value as CampaignType)}
                  className={errors.type ? inputErrorClass : inputClass}
                  disabled={isPending}
                >
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {formData.type && (
                  <p className="text-[11px] text-slate-400 mt-1">
                    {TYPE_OPTIONS.find((t) => t.value === formData.type)?.description}
                  </p>
                )}
              </FormField>
            </div>

            {/* Platforms */}
            <FormField label="Piattaforme" required error={errors.platforms}>
              <div className="flex flex-wrap gap-2 mt-0.5">
                {PLATFORM_OPTIONS.map((opt) => {
                  const selected = formData.platforms.includes(opt.value)
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => togglePlatform(opt.value)}
                      disabled={isPending}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors',
                        selected
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50 hover:border-slate-300',
                        'disabled:opacity-50 disabled:cursor-not-allowed'
                      )}
                    >
                      {opt.label}
                    </button>
                  )
                })}
              </div>
            </FormField>

            {/* Budget */}
            <FormField
              label="Budget Totale (€)"
              required
              error={errors.totalBudget}
              hint="Budget complessivo della campagna in euro"
            >
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.totalBudget}
                  onChange={(e) => handleChange('totalBudget', e.target.value)}
                  placeholder="0.00"
                  className={cn(
                    errors.totalBudget ? inputErrorClass : inputClass,
                    'pl-7 tabular-nums'
                  )}
                  disabled={isPending}
                />
              </div>
            </FormField>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Data Inizio" required error={errors.startDate}>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className={cn(errors.startDate ? inputErrorClass : inputClass, 'tabular-nums')}
                  disabled={isPending}
                />
              </FormField>

              <FormField
                label="Data Fine"
                error={errors.endDate}
                hint={formData.type === 'ALWAYS_ON' ? 'Opzionale per campagne Always On' : undefined}
              >
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  min={formData.startDate || undefined}
                  className={cn(errors.endDate ? inputErrorClass : inputClass, 'tabular-nums')}
                  disabled={isPending}
                />
              </FormField>
            </div>

            {/* Objective */}
            <FormField
              label="Obiettivo"
              hint="es. Brand Awareness, Lead Generation, Conversioni, Traffico"
            >
              <input
                type="text"
                value={formData.objective}
                onChange={(e) => handleChange('objective', e.target.value)}
                placeholder="es. Brand Awareness"
                className={inputClass}
                disabled={isPending}
              />
            </FormField>

            {/* Target audience */}
            <FormField
              label="Target Audience"
              hint="Descrizione del pubblico di riferimento"
            >
              <input
                type="text"
                value={formData.targetAudience}
                onChange={(e) => handleChange('targetAudience', e.target.value)}
                placeholder="es. Donne 25-45, interessate a moda e lifestyle"
                className={inputClass}
                disabled={isPending}
              />
            </FormField>

            {/* Notes */}
            <FormField label="Note">
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Note interne sulla campagna, brief creativo, ecc."
                rows={3}
                className={cn(inputClass, 'resize-none')}
                disabled={isPending}
              />
            </FormField>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
            <p className="text-xs text-slate-400">
              I campi con <span className="text-danger-600">*</span> sono obbligatori
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { if (!isPending) onClose() }}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isPending}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg',
                  'hover:bg-primary-700 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isPending
                  ? (isEdit ? 'Salvataggio...' : 'Creazione...')
                  : (isEdit ? 'Salva Modifiche' : 'Crea Campagna')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
